import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import {toast} from "react-toastify";
import {suggest} from "../../../services/controlPanelApi";
import {useLocations} from "../../../utils/LocationContext";
import './ContactStep.css';

const ContactStep = ({ formData, updateFormData, errors, showCuit = false, isEditing = false }) => {
    const { locations: prefetchedLocations } = useLocations();
    // Reemplaza el fetch sincrónico de getLocations() — vienen prefetcheadas en LocationContext.
    const availableLocations = prefetchedLocations;
    const availableZones = useMemo(
        () => [...new Set((prefetchedLocations || []).map((loc) => loc.zone))],
        [prefetchedLocations]
    );
    const [instagramUsername, setInstagramUsername] = useState('');
    const [websiteLink, setWebsiteLink] = useState({ name: 'Sitio Web', url: '' });
    const [websiteDisplayUrl, setWebsiteDisplayUrl] = useState('');
    const [newBranch, setNewBranch] = useState('');
    const [hasUsedZoneButtons, setHasUsedZoneButtons] = useState(false);

    // Modal de sugerencia de ubicación
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationSuggestion, setLocationSuggestion] = useState('');

    const removeProtocol = (url) => url.replace(/^https?:\/\//, '');

    const addProtocolIfMissing = (url) => {
        if (!url.trim()) return '';
        if (!/^https?:\/\//.test(url)) return `https://${url}`;
        return url;
    };

    useEffect(() => {
        if (formData.links && formData.links.length > 0) {
            const instagram = formData.links.find(link => link.name === 'Instagram');
            const website = formData.links.find(link => link.name === 'Sitio Web');

            if (instagram) {
                const username = instagram.url.replace('https://www.instagram.com/', '');
                setInstagramUsername(username);
            }

            if (website) {
                setWebsiteLink(website);
                setWebsiteDisplayUrl(removeProtocol(website.url));
            }
        }
    }, [formData.links]);

    useEffect(() => {
        const links = [];
        if (instagramUsername.trim()) {
            const sanitizedUsername = instagramUsername.trim().replace(/^@/, '');
            if (sanitizedUsername) {
                links.push({ name: 'Instagram', url: `https://www.instagram.com/${sanitizedUsername}` });
            }
        }
        if (websiteLink.url.trim()) links.push(websiteLink);
        updateFormData('links', links);
    }, [instagramUsername, websiteLink]);

    const handlePhoneChange = (value) => {
        let numericValue = value.replace(/\D/g, '');
        // Si comienza con 0, eliminarlo
        if (numericValue.startsWith('0')) {
            numericValue = numericValue.substring(1);
        }
        updateFormData('phone', numericValue);
    };

    const handleCuitChange = (value) => {
        const numericValue = value.replace(/\D/g, '').slice(0, 11);
        updateFormData('cuit', numericValue);
    };

    const handleWebsiteUrlChange = (displayUrl) => {
        setWebsiteDisplayUrl(displayUrl);
        const fullUrl = addProtocolIfMissing(displayUrl);
        setWebsiteLink({ ...websiteLink, url: fullUrl });
    };

    const handleCitySelect = (selectedOptions) => {
        const selectedCities = selectedOptions.map(option => option.value);
        updateFormData('locations', selectedCities);
    };

    const selectZone = (zoneName) => {
        const zoneLocations = availableLocations
            .filter(location => location.zone === zoneName)
            .map(location => location.name);

        // Obtener ubicaciones ya seleccionadas y agregar las de la zona
        const currentLocations = formData.locations || [];
        const newLocations = [...new Set([...currentLocations, ...zoneLocations])];

        updateFormData('locations', newLocations);
        setHasUsedZoneButtons(true);
    };

    const addBranch = () => {
        const value = (newBranch || '').trim();
        if (!value) return;
        const current = formData.branches || [];
        if (current.includes(value)) {
            setNewBranch('');
            return;
        }
        updateFormData('branches', [...current, value]);
        setNewBranch('');
    };

    const removeBranch = (index) => {
        const updatedBranches = (formData.branches || []).filter((_, i) => i !== index);
        updateFormData('branches', updatedBranches);
    };

    // Modal: sugerir ubicación
    const addLocationSuggestion = async () => {
        const val = (locationSuggestion || '').trim();
        if (!val) return;

        // Si estamos en modo edición, enviar al servidor en lugar de agregar al formulario
        if (isEditing) {
            try {
                await suggest({
                    userId: formData.username,
                    suggestion: `Nueva ubicación sugerida: ${val}`,
                    type: 'location',
                });
                toast.success('Tu sugerencia ha sido enviada y será revisada por el equipo');
                setLocationSuggestion('');
                setShowLocationModal(false);
            } catch (error) {
                console.error('Error al enviar sugerencia:', error);
                toast.error('No se pudo enviar la sugerencia. Inténtalo nuevamente.');
            }
            return;
        }

        // Comportamiento original para registro
        const suggested = `${val} (sugerencia)`;
        if (!formData.locations?.includes(suggested)) {
            updateFormData('locations', [...(formData.locations || []), suggested]);
        }
        setLocationSuggestion('');
        setShowLocationModal(false);
    };

    const modalStyles = {
        overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
        content: { background: 'var(--card-background-dark)', border: '1px solid var(--input-border-dark)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.35)' },
        header: { padding: '16px 20px', borderBottom: '1px solid var(--input-border-dark)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
        body: { padding: 20 },
        footer: { padding: '0 20px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end' }
    };

    const cityOptions = availableLocations.map(location => ({
        value: location.name,
        label: location.name,
        zone: location.zone
    }));

    return (
        <div className="step-content-wrapper">
            <div className="step-header">
                <h2>Información de Contacto</h2>
                <p>Configura cómo los clientes pueden contactarte y encontrarte</p>
            </div>

            <div className="form-section">
                <div className="form-group">
                    <label htmlFor="phone">
                        Teléfono <span className="required">*</span>
                    </label>
                    <input
                        id="phone"
                        type="tel"
                        className={`form-input ${errors.phone ? 'error' : ''}`}
                        placeholder="Ej: 2216973030"
                        value={formData.phone || ''}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                    <div className="field-help">Solo números, sin cero al comienzo, ni espacios o guiones.</div>
                </div>

                {showCuit && (
                    <div className="form-group">
                        <label htmlFor="cuit">
                            CUIT o CUIL <span className="required">*</span>
                        </label>
                        <input
                            id="cuit"
                            type="tel"
                            className={`form-input ${errors.cuit ? 'error' : ''}`}
                            placeholder="11 dígitos"
                            value={formData.cuit || ''}
                            onChange={(e) => handleCuitChange(e.target.value)}
                            maxLength={11}
                        />
                        {errors.cuit && <span className="error-message">{errors.cuit}</span>}
                        <div className="field-help">Solo números, exactamente 11 dígitos</div>
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="timetable">Horarios de atención</label>
                    <textarea
                        id="timetable"
                        className="form-textarea"
                        placeholder="Ej: Lunes a Viernes: 9:00 - 18:00&#10;Sábados: 9:00 - 14:00&#10;Domingos: Cerrado"
                        value={formData.timetable || ''}
                        onChange={(e) => updateFormData('timetable', e.target.value)}
                        rows={4}
                    />
                    <div className="field-help">Especifica tus horarios de trabajo</div>
                </div>

                <div className="form-group">
                    <label>Redes Sociales y Sitio Web</label>
                    <div className="social-input-group">
                        <div className="field-help">Usuario de Instagram (sin el @)</div>
                        <div className="input-with-prefix">
                            <span className="input-prefix">@</span>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="usuario_instagram"
                                value={instagramUsername}
                                onChange={(e) => setInstagramUsername(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="website-input-group">
                        <div className="field-help">Tu sitio web (se agregará https:// automáticamente)</div>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="miempresa.com"
                            value={websiteDisplayUrl}
                            onChange={(e) => handleWebsiteUrlChange(e.target.value)}
                        />
                    </div>

                    <div className="field-help">Los enlaces vacíos no se guardarán</div>
                </div>

                <div className="form-group">
                    <label htmlFor="locations">
                        Zonas de trabajo <span className="required">*</span>
                    </label>
                    <div className="field-help">
                        {formData.type === "service" ? "Selecciona todas las zonas donde ofreces tus servicios" : "Selecciona todas las zonas donde vendes o envías tus productos"}
                    </div>

                    {/* Botones de selección rápida por zona */}
                    {availableZones.length > 0 && (
                        <div className="zone-buttons">
                            <div className="zone-buttons-container">
                                {availableZones.map(zone => (
                                    <button
                                        key={zone}
                                        type="button"
                                        className="zone-button"
                                        onClick={() => selectZone(zone)}
                                    >
                                        📍 {zone} y alrededores
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <Select
                        id="locations"
                        isMulti
                        options={cityOptions}
                        classNamePrefix="react-select"
                        className="react-select-container-bordered"
                        onChange={handleCitySelect}
                        placeholder="Selecciona ciudades"
                        noOptionsMessage={() => "No hay más opciones. Podés escribir una."}
                        // Si LocationContext todavía no cargó cityOptions, igual mostramos
                        // las ubicaciones guardadas como chips (fallback {value,label}).
                        value={(formData.locations || []).map((name) =>
                            cityOptions.find((option) => option.value === name) || { value: name, label: name }
                        )}
                        // Props defensivas para evitar carreras de DOM del menú
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        closeMenuOnScroll={true}
                    />

                    <div className="field-footer">
                        {errors.locations && <span className="error-message">{errors.locations}</span>}

                        {/* Mensaje condicional cuando se usan botones de zona */}
                        {hasUsedZoneButtons && (
                            <div className="zone-tip-message">
                                💡 <strong>Tip:</strong> Dejá únicamente las zonas que cubrás. Podés quitar ubicaciones haciendo clic en la "×" de cada una.
                            </div>
                        )}

                        <div className="provider-suggestion-link" style={{ marginTop: 6 }}>
                            <button
                                type="button"
                                onClick={() => setShowLocationModal(true)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    color: 'var(--primary-color)',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}>
                                Sugerir otra ubicación
                            </button>
                        </div>
                    </div>
                </div>

                {formData.type === "products" && (
                    <div className="form-group">
                        <label>Sucursales o Puntos de venta</label>

                        <div className="add-field-container">
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Nombre y dirección de la sucursal"
                                value={newBranch}
                                onChange={(e) => setNewBranch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBranch(); } }}
                            />
                            <button type="button" className="btn btn-outline add-btn" onClick={addBranch}>Agregar</button>
                        </div>
                        <div className="field-help">Agrega las direcciones de tus sucursales, locales comerciales o puntos de venta físicos</div>


                        <div className="selected-items">
                            {formData.branches?.length > 0 ? (
                                formData.branches.map((branch, index) => (
                                    <div key={index} className="selected-item">
                                        <span>{branch}</span>
                                        <button type="button" className="remove-btn" onClick={() => removeBranch(index)}>×</button>
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state compact">No hay sucursales cargadas</div>
                            )}
                        </div>
                    </div>
                )}

            </div>

            {/* Modal de sugerencia de ubicación */}
            {showLocationModal && (
                <div style={modalStyles.overlay}>
                    <div style={modalStyles.content}>
                        <div style={modalStyles.header}>
                            <h3 style={{ margin: 0 }}>Sugerir nueva ubicación</h3>
                            <button className="remove-btn" onClick={() => setShowLocationModal(false)} title="Cerrar">×</button>
                        </div>
                        <div style={modalStyles.body}>
                            <p>¿Qué ubicación te gustaría agregar? Tu sugerencia será revisada por nuestro equipo.</p>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Escribe tu sugerencia de ubicación"
                                value={locationSuggestion}
                                onChange={(e) => setLocationSuggestion(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLocationSuggestion(); } }}
                            />
                        </div>
                        <div style={modalStyles.footer}>
                            <button className="btn btn-secondary" onClick={() => setShowLocationModal(false)}>Cancelar</button>
                            <button className="btn btn-primary" onClick={addLocationSuggestion} disabled={!locationSuggestion.trim()}>Agregar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactStep;
